import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import {
    // ... imports de query.util
    andAllWhere,
    andWhere,
    buildQuery,
    CombinedFilter,
    FilterFn,
    noop,
    Ops,
    orderBy,
    OrderVariant,
    orWhere,
    page,
    where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { IPagination, IPaginationResult, IRequestResult } from "app/common/interfaces";
import { Observable } from "rxjs";
import { TemporaryPasswordAttributes } from "@albatrosdeveloper/ave-models-package";
import { ICreateTemporaryPassword, IResponseCreateTemporaryPassword, IUpdateTemporaryPassword } from "./dto/temporary-password.interface";
import { AbstractService } from "app/services/abstract.service";
import { environment } from "environments/environment.prod";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";

const apiUrl = environment.API_AUTHENTICATION_URL;
const moduleName = "temporary-passwords";

@Injectable({
    providedIn: "root",
})
export class TemporaryPasswordService extends AbstractService<
    TemporaryPasswordAttributes,
    ICreateTemporaryPassword,
    IUpdateTemporaryPassword
> {

    /** The last filter used to load the list */
    currentFilter: CombinedFilter<TemporaryPasswordAttributes>;    

    constructor(
        http: HttpClient,
    ) {
        super(apiUrl, moduleName, http);
    }

    override create(body: ICreateTemporaryPassword): Observable<IRequestResult<IResponseCreateTemporaryPassword>> {
        const URL = `${apiUrl}api//${moduleName}`;
        
        return this.http.post<IRequestResult<IResponseCreateTemporaryPassword>>(URL, body);
    }

    /**
     * Pagination
     * @param options Pagination options
     * @returns Paginated data
     */
    findAllWithCount(
        options: IPagination<TemporaryPasswordAttributes>,
        dataToFilter?: IFilters,
        statusFilter?: Number[],
    ): Observable<IPaginationResult<TemporaryPasswordAttributes>> {
        const hasSearch = !!options.search;

        const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<TemporaryPasswordAttributes>[] => {
            const deletedFilter: FilterFn<TemporaryPasswordAttributes> = params.useOrWhere
            ? orWhere('_deleted', Ops.eq(false))
            : where('_deleted', Ops.eq(false));

            const filters: FilterFn<TemporaryPasswordAttributes>[] = [deletedFilter];

            if (dataToFilter?.assignedUserId?.length > 0) {
                filters.push(
                    andWhere("targetUser.username" as keyof TemporaryPasswordAttributes, Ops.in(...dataToFilter.assignedUserId))
                );
            }

            if (dataToFilter?.solicitedUserId?.length > 0) {
                filters.push(
                    andWhere("requestedBy.username" as keyof TemporaryPasswordAttributes, Ops.in(...dataToFilter.solicitedUserId))
                );
            }

            if (dataToFilter?.startDate && dataToFilter?.endDate) {
                filters.push(andWhere("createdAt", Ops.btwnDates(dataToFilter.startDate, dataToFilter.endDate)));
            }

            return filters;
        };

        const searchByReasonQueryObject: FilterFn<TemporaryPasswordAttributes>[] = [
            ...buildBaseFilters({ useOrWhere: false }),
            hasSearch ? andWhere("reason", Ops.like(options.search!, "i")) : noop()
        ];

        const searchByTargetUserQueryObject: FilterFn<TemporaryPasswordAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("targetUser.username" as keyof TemporaryPasswordAttributes, Ops.like(options.search!, "i"))
        ] : [];

        const searchByRequestedByQueryObject: FilterFn<TemporaryPasswordAttributes>[] = hasSearch ? [
            ...buildBaseFilters({ useOrWhere: true }),
            andWhere("requestedBy.username" as keyof TemporaryPasswordAttributes, Ops.like(options.search!, "i"))
        ] : [];

        const query = buildQuery<TemporaryPasswordAttributes>(
            ...searchByReasonQueryObject,
            ...searchByTargetUserQueryObject,
            ...searchByRequestedByQueryObject,

            orderBy(
            options.sort ?? "createdAt",
            options.sortDirection === "DESC" ? OrderVariant.Desc : OrderVariant.Asc
            ),
            page(options.currentPage ?? 1, options.limitPerPage ?? 100)
        );

        this.currentFilter = query;

        const API_URL: string =
            apiUrl +
            `api/${moduleName}/withCount`;
        return this.http.post<IPaginationResult<TemporaryPasswordAttributes>>(API_URL, { filter: query, statusFilter: statusFilter ?? []});
    }

    revoked(id: string): Observable<IRequestResult<TemporaryPasswordAttributes>> {
        const URL = `${apiUrl}api//${moduleName}/revoke/${id}`;
        
        return this.http.patch<IRequestResult<TemporaryPasswordAttributes>>(URL, {});
    }

    delete(id: string): Observable<IRequestResult<TemporaryPasswordAttributes>> {
        const URL = `${apiUrl}api//${moduleName}/${id}`;
        
        return this.http.delete<IRequestResult<TemporaryPasswordAttributes>>(URL, {});
    }
}